import React, { useState, useEffect } from 'react';
import BackOfficeLayout from '../../components/backoffice/BackOfficeLayout';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { XCircle, Loader2, RefreshCw } from 'lucide-react';
import { toast } from '../../lib/safeToast';
import { getApiErrorMessage } from '../../lib/utils';

const BackOfficeMerchantSuspended = () => {
  const { api } = useAuth();
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, per_page: 25, total: 0, last_page: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [activatingId, setActivatingId] = useState(null);

  const fetchSuspended = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: 25, status: 'suspended' });
      const res = await api.get(`/backoffice/merchants?${params}`);
      setData(res.data?.data || []);
      setMeta(res.data?.meta || { current_page: 1, per_page: 25, total: 0, last_page: 0 });
    } catch {
      toast.error('Failed to load suspended merchants');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuspended();
  }, [page]);

  const handleActivate = async (userId) => {
    setActivatingId(userId);
    try {
      await api.patch(`/users/${userId}`, { account_status: 'active' });
      toast.success('Merchant activated');
      fetchSuspended();
    } catch (err) {
      toast.error(getApiErrorMessage(err.response?.data?.detail, 'Failed to activate'));
    } finally {
      setActivatingId(null);
    }
  };

  return (
    <BackOfficeLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between pb-4">
          <div>
            <h1 className="text-2xl font-bold text-[#073B2E] flex items-center gap-2">
              <XCircle className="w-7 h-7 text-[#0B6445]" />
              Suspended Merchants
            </h1>
            <p className="text-[#0E1B16]/70 mt-2">Merchants that have been suspended. Activate here or manage in All Merchants.</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchSuspended} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
        <Card className="border-[#DCE4E0] shadow-sm">
          <CardContent className="pt-6">
            {loading ? (
              <div className="flex justify-center py-12"><div className="spinner w-8 h-8" /></div>
            ) : data.length === 0 ? (
              <p className="text-center py-12 text-[#0E1B16]/60">No suspended merchants.</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#DCE4E0]">
                        <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Name</th>
                        <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Email</th>
                        <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Role</th>
                        <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">KYC Status</th>
                        <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Created</th>
                        <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.map((row) => (
                        <tr key={row.id} className="border-b border-[#DCE4E0]/80 hover:bg-[#F8FAFC]">
                          <td className="py-3 px-2">{row.name || '—'}</td>
                          <td className="py-3 px-2">{row.email}</td>
                          <td className="py-3 px-2">{row.role || '—'}</td>
                          <td className="py-3 px-2">
                            <span className={row.kyc_status === 'approved' ? 'text-green-600' : row.kyc_status === 'in_review' ? 'text-amber-600' : 'text-slate-500'}>
                              {row.kyc_status || 'pending'}
                            </span>
                          </td>
                          <td className="py-3 px-2">{row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}</td>
                          <td className="py-3 px-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-900"
                              onClick={() => handleActivate(row.id)}
                              disabled={activatingId === row.id}
                            >
                              {activatingId === row.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Activate'}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {meta.last_page > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#DCE4E0]">
                    <p className="text-sm text-[#0E1B16]/70">Page {meta.current_page} of {meta.last_page} ({meta.total} total)</p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                        Previous
                      </Button>
                      <Button variant="outline" size="sm" disabled={page >= meta.last_page} onClick={() => setPage((p) => p + 1)}>
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </BackOfficeLayout>
  );
};

export default BackOfficeMerchantSuspended;
